import { EventEmitter }             from 'events';
import {
  replace,
  freeze,
}                                   from 'rewire-core';
import { Modal, IModalState, Form } from 'rewire-ui';
import setViewOnlyForFormFields     from '../utility/setViewOnlyForFormFields';
import { confirmationModel }        from '../ui/ConfirmationDialog';
import { notifications }            from '../ui/Notifications';

export type TEditLoadFn   = (ids: string[])               => Promise<any[]  | undefined>;
export type TEditSaveFn   = (schema: string, objs: any[]) => Promise<any[]  | undefined>;
export type TEditDeleteFn = (ids: string[])               => Promise<string | boolean | undefined>;
export type AccessFn      = ()                            => boolean;

export interface IEditDialogModelState extends IModalState {
  id?:       string;
  visible?:  boolean;
  isLoading: boolean;
}

export default abstract class EditDialogModel<TState = unknown> extends Modal<IEditDialogModelState & TState> {
  public    form:       Form;
  public    isModal:    boolean;
  public    error:      boolean;
  private   __onEvent:  EventEmitter;

  protected constructor(private _title: string, protected _load: TEditLoadFn, protected _insert: TEditSaveFn, protected _update: TEditSaveFn, protected _remove: TEditDeleteFn, protected _toObject?: any, protected _canAdd?: AccessFn, protected _canEdit?: AccessFn, protected _canDelete?: AccessFn) {
    super();
    this.isLoading = false;
    this.form      = this._createForm();
    this.isModal   = true;
    this.error     = false;
    setViewOnlyForFormFields(this.form, () => this.viewOnly, this.viewOnlyAccessExceptionsFieldNames);
    this.action('save',   this.submit,        { icon: 'save', variant: 'contained', color: 'primary', disabled: () => this.viewOnly || this.hasErrors() || !this.hasChanges(), type: 'submit' })
        .action('ok',     this.submit,        { variant: 'contained', color: 'primary', disabled: () => this.viewOnly || this.hasErrors() || !this.hasChanges(), type: 'submit' })
        .action('apply',  this.submit,        { variant: 'contained', color: 'primary', disabled: () => this.viewOnly || this.hasErrors() || !this.hasChanges(), type: 'submit' })
        .action('undo',   this.undo,          { variant: 'contained', disabled: () => this.viewOnly || this.isNew || !this.hasChanges() })
        .action('delete', this.remove as any, { variant: 'contained', icon: 'delete', disabled: () => !this.canDelete })
        .action('close',  { icon: 'cancel' })
        .action('cancel', { icon: 'cancel' });


    this.state.title = this.getTitle();

    this.__onEvent   = new EventEmitter();
  }

  get onEvent() {
    return this.__onEvent;
  }

  get id(): string | undefined {
    return this.state.id;
  }

  set id(value: string | undefined) {
    this.state.id = value;
  }

  get visible() {
    return this.state.visible !== false;
  }

  set visible(value: boolean) {
    this.state.visible = value;
    this.state.title   = this.getTitle();
  }

  get canAdd(): boolean {
    return this._canAdd?.() ?? true;
  }

  get canEdit(): boolean {
    return this._canEdit?.() ?? true;
  }

  get canDelete(): boolean {
    return this._canDelete?.() ?? true;
  }

  get viewOnly(): boolean {
    return !((this.canAdd && this.isNew) || (this.canEdit && !this.isNew));
  }

  get isLoading(): boolean {
    return this.state.isLoading;
  }

  set isLoading(value: boolean) {
    this.state.isLoading = value;
  }

  reset(defaultObj?: any) {
    this._reset(defaultObj);
    this.isLoading = false;
  }

  _reset(defaultObj?: any) {
    if (defaultObj) {
      freeze(() => {
        this.form.fields.forEach(field => {
          field.value = defaultObj[field.name];
          field.error = undefined;
        });
        replace((this.form as any)._value, defaultObj);
      });
    } else {
      this.form.clear(true);
    }
    this.id = undefined;
  }

  protected get name() { return (this.form.field.name?.value) ?? ''; }

  protected getTitle() {
    if (this.viewOnly || !this.visible) {
      return this._title;
    }

    if (this.isNew) {
      return `Add New ${this._title}`;
    }
    const name = this.name;
    if (name) {
      return `Editing ${this._title} - ${name}`;
    }
    return `Editing ${this._title}`;
  }

  get isNew() {
    return !this.id;
  }

  protected hasChanges(): boolean {
    return this.form.hasChanges;
  }

  protected hasErrors(): boolean {
    return this.form.hasErrors;
  }

  protected abstract _createForm(): Form;

  protected get viewOnlyAccessExceptionsFieldNames(): string[] {
    return [];
  }

  public mapToSave(): ObjectType {
    const obj: any = this.isNew ? this.form.toObjectValues() : this.form.toObject();
    if (!this.isNew) obj.id = this.id;
    return obj;
  }

  public mapToForm(obj: ObjectType): ObjectType { return obj; }

  async load(id?: string, isModal: boolean = true) {
    this.isLoading = true;
    this.id        = id;
    this.isModal   = isModal;
    if (id) {
      const results = await this._load([id]);
      if (!results || (results.length !== 1) || !results[0]) {
        this._reset();
      } else {
        this.form.value = this.mapToForm(results[0]);
      }
    }
    else {
      this._reset();
    }
    this.state.title = this.getTitle();
    this.open();
    this.isLoading = false;
  }

  undo() {
    this.form.revert();
    return false;
  }

  remove() {
    return new Promise((resolve) => {
      this.error = false;
      if (this.isNew) {
        resolve(true);
        return true;
      }

      confirmationModel.load(
        `Please confirm you wish to remove ${this.name}`,
        {
          yesAction: async () => {
            try {
              const result = await this._remove([this.id!]);
              if (result) {
                notifications.success(`${this._title} ${this.name} was successfuly removed`);
                this._reset();
                resolve(true);
                this.onEvent.emit('remove', true);
                return true;
              }
            } catch (err) {}
            notifications.error(`There was an error removing ${this._title.toLowerCase()} ${this.name}`);
            this.error = true;
            resolve(true);
            return true;
          },
          noAction: () => (this.error = true, resolve(false), true),
          maxWidth: 'sm', title: `Remove ${this.name}`
        }
      );
    });
  }

  protected async save(): Promise<boolean> {
    if (this.viewOnly) return false;
    this.error = false;
    try {
      const obj = this._toObject ? this._toObject() : this.mapToSave();
      if (this._toObject && typeof this._toObject === 'function' && !this.isNew) {
        obj.id = this.id;
      }
      const result = await ((this.isNew) ? this._insert('id', [obj]) : this._update('id', [obj]));
      if (result && (result.length > 0)) {
        const r          = result[0];
        this.id          = r.id;
        this.state.title = this.getTitle();
        notifications.success(`${this._title} ${this.name} was successfully saved`);
        if (this.isModal) {
          this._reset();
        }
        return true;
      }
      notifications.error(`There was an error saving ${this._title.toLowerCase()} ${this.name}`);
      this.error = true;
    } catch (err) {
      notifications.error(`There was an error saving ${this._title.toLowerCase()} ${this.name}`);
      this.error = true;
    }

    return !this.error;
  }

  async submit() {
    if (!this.form || this.viewOnly) return false;
    if (!await this.form.submitAsync(() => this.save()))
      return false;
    this.onEvent.emit('submit', true);
    return this.isModal;
  }
}
